import { useEffect, useRef, useState, type FormEvent } from "react";
import { Check, Download, FileJson, LoaderCircle, Save, Upload } from "lucide-react";
import { portalApi, type PortalApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { EmptyState, ErrorState, Field, LoadingState, PageHeader } from "../components/Shared";
import { useAsyncData } from "../hooks/useAsyncData";
import type { ScriptProfile } from "../types";
import { calculateWordTargets, formatWordRange } from "../utils/wordTargets";

export function ProfilesPage({ client = portalApi }: { client?: PortalApiClient }) {
  const { session } = useAuth();
  const catalog = useAsyncData(() => client.catalog.get(), [client]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [json, setJson] = useState("{}");
  const [isNeutral, setIsNeutral] = useState(true);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const selected = catalog.data?.profiles.find((profile) => profile.id === selectedId) ?? null;

  useEffect(() => {
    if (!selectedId && catalog.data?.profiles.length) setSelectedId(catalog.data.profiles[0].id);
  }, [catalog.data, selectedId]);
  useEffect(() => {
    if (!selected) return;
    setName(selected.name);
    setJson(JSON.stringify(selected.profile, null, 2));
    setIsNeutral(selected.isNeutral);
    setActive(selected.active);
    setError(null);
    setSaved(false);
  }, [selected]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    let profile: Record<string, unknown>;
    try {
      profile = JSON.parse(json) as Record<string, unknown>;
    } catch {
      setError("The profile JSON is not valid.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = selected
        ? await client.profiles.update(selected.id, { name: name.trim(), profile, isNeutral, active })
        : await client.profiles.create({ name: name.trim(), profile, isNeutral });
      await catalog.reload();
      setSelectedId(result.id);
      setSaved(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "The profile could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  async function importProfile(file: File) {
    setError(null);
    try {
      const parsed = JSON.parse(await file.text()) as Record<string, unknown>;
      const embedded = parsed.profile;
      const profile =
        embedded && typeof embedded === "object" && !Array.isArray(embedded)
          ? (embedded as Record<string, unknown>)
          : parsed;
      const result = await client.profiles.create({
        name: typeof parsed.name === "string" ? parsed.name : file.name.replace(/\.json$/i, ""),
        profile,
        isNeutral: parsed.is_neutral !== false,
      });
      await catalog.reload();
      setSelectedId(result.id);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "The profile could not be imported.");
    }
  }

  async function exportProfile(profile: ScriptProfile) {
    const exported = await client.profiles.export(profile.id);
    const blob = new Blob([JSON.stringify(exported, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${profile.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  if (catalog.loading) return <LoadingState label="Loading script profiles" />;
  if (catalog.error || !catalog.data) {
    return <ErrorState message={catalog.error ?? "Profiles are unavailable."} onRetry={catalog.reload} />;
  }

  return (
    <>
      <PageHeader
        title="Script profiles"
        description="Manage reusable writing settings. Defaults are set separately for each channel."
        actions={
          <>
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void importProfile(file);
                event.target.value = "";
              }}
            />
            <button className="button secondary" type="button" onClick={() => fileInput.current?.click()}>
              <Upload size={16} />
              Import JSON
            </button>
            <button
              className="button primary"
              type="button"
              onClick={() => {
                setSelectedId(null);
                setName("");
                setJson("{}");
                setIsNeutral(true);
                setActive(true);
              }}
            >
              <FileJson size={16} />
              New profile
            </button>
          </>
        }
      />
      <section className="profile-layout">
        <aside className="profile-list" aria-label="Profiles">
          {catalog.data.profiles.length ? (
            catalog.data.profiles.map((profile) => (
              <button
                type="button"
                key={profile.id}
                className={profile.id === selectedId ? "active" : ""}
                onClick={() => setSelectedId(profile.id)}
              >
                <span>
                  <strong>{profile.name}</strong>
                  <small>
                    v{profile.version} / {profile.hash.slice(0, 10)} / {profile.active ? "active" : "inactive"}
                  </small>
                </span>
              </button>
            ))
          ) : (
            <EmptyState title="No profiles" message="Import or create the first profile." />
          )}
        </aside>
        <form className="profile-editor" onSubmit={save}>
          <div className="editor-toolbar">
            <div>
              <h2>{selected ? selected.name : "New profile"}</h2>
              {selected ? <span>Updated {new Date(selected.updatedAt).toLocaleString()}</span> : null}
            </div>
            <div className="page-actions">
              {selected ? (
                <button className="button secondary" type="button" onClick={() => void exportProfile(selected)}>
                  <Download size={16} />
                  Export
                </button>
              ) : null}
              <button className="button primary" type="submit" disabled={saving || !name.trim()}>
                {saving ? <LoaderCircle className="spin" size={16} /> : saved ? <Check size={16} /> : <Save size={16} />}
                {saved ? "Saved" : "Save profile"}
              </button>
            </div>
          </div>
          <div className="form-grid two-columns">
            <Field label="Profile name">
              <input required value={name} onChange={(event) => setName(event.target.value)} />
            </Field>
            <div className="checkbox-stack">
              <label>
                <input type="checkbox" checked={isNeutral} onChange={(event) => setIsNeutral(event.target.checked)} />
                Neutral documentary profile
              </label>
              {selected ? (
                <label>
                  <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
                  Available for new jobs
                </label>
              ) : null}
            </div>
          </div>
          <Field label="Profile JSON" hint="The server validates the stored profile before it can be used.">
            <textarea className="code-editor" rows={24} spellCheck={false} value={json} onChange={(event) => setJson(event.target.value)} />
          </Field>
          {error ? <p className="form-alert" role="alert">{error}</p> : null}
        </form>
      </section>
      {session?.user.role === "owner" ? (
        <ChannelDefaults client={client} catalog={catalog.data} onSaved={catalog.reload} />
      ) : null}
    </>
  );
}

function ChannelDefaults({
  client,
  catalog,
  onSaved,
}: {
  client: PortalApiClient;
  catalog: Awaited<ReturnType<PortalApiClient["catalog"]["get"]>>;
  onSaved(): Promise<void>;
}) {
  const [saving, setSaving] = useState<string | null>(null);
  return (
    <section className="content-section">
      <div className="section-heading">
        <div>
          <h2>Channel defaults</h2>
          <p>These choices prefill the approval screen for new jobs.</p>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Channel</th><th>Profile</th><th>Voice</th><th>Duration</th><th /></tr>
          </thead>
          <tbody>
            {catalog.channels.map((channel) => (
              <ChannelDefaultRow
                key={channel.id}
                channel={channel}
                catalog={catalog}
                saving={saving === channel.id}
                onSave={async (input) => {
                  setSaving(channel.id);
                  try {
                    await client.channels.updateDefaults(channel.id, input);
                    await onSaved();
                  } finally {
                    setSaving(null);
                  }
                }}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ChannelDefaultRow({
  channel,
  catalog,
  saving,
  onSave,
}: {
  channel: Awaited<ReturnType<PortalApiClient["catalog"]["get"]>>["channels"][number];
  catalog: Awaited<ReturnType<PortalApiClient["catalog"]["get"]>>;
  saving: boolean;
  onSave(input: { defaultProfileId: string; defaultVoiceId: string; defaultDurationMinutes: number }): Promise<void>;
}) {
  const [profileId, setProfileId] = useState(channel.defaultProfileId ?? "");
  const [voiceId, setVoiceId] = useState(channel.defaultVoiceId ?? "");
  const [minutes, setMinutes] = useState(channel.defaultDurationMinutes);
  const targets = calculateWordTargets(minutes);
  return (
    <tr>
      <td><strong>{channel.name}</strong></td>
      <td>
        <select aria-label={`Default profile for ${channel.name}`} value={profileId} onChange={(event) => setProfileId(event.target.value)}>
          <option value="">Choose</option>
          {catalog.profiles.filter((profile) => profile.active).map((profile) => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
        </select>
      </td>
      <td>
        <select aria-label={`Default voice for ${channel.name}`} value={voiceId} onChange={(event) => setVoiceId(event.target.value)}>
          <option value="">Choose</option>
          {catalog.voices.filter((voice) => voice.active).map((voice) => <option key={voice.id} value={voice.id}>{voice.name}</option>)}
        </select>
      </td>
      <td>
        <input aria-label={`Default duration for ${channel.name}`} type="number" min={1} max={180} value={minutes} onChange={(event) => setMinutes(Number(event.target.value))} />
        <small>{formatWordRange(targets)}</small>
      </td>
      <td>
        <button className="button secondary small" type="button" disabled={saving || !profileId || !voiceId} onClick={() => void onSave({ defaultProfileId: profileId, defaultVoiceId: voiceId, defaultDurationMinutes: minutes })}>
          {saving ? <LoaderCircle className="spin" size={15} /> : <Save size={15} />}
          Save
        </button>
      </td>
    </tr>
  );
}
